import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle } from 'lucide-react';

const MAP_URL =
  'https://www.openstreetmap.org/export/embed.html?bbox=13.195%2C52.612%2C13.220%2C52.625&layer=mapnik&marker=52.6175%2C13.2058';

const anliegen = ['Gardinen', 'Polsterei', 'Sonnenschutz', 'Insektenschutz', 'Teppich', 'Waschservice', 'Sonstiges'];

const Contact = () => {
  const [form, setForm] = useState({
    name: '', email: '', telefon: '', anliegen: '', nachricht: '', datenschutz: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Bitte geben Sie Ihren Namen an.';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Bitte geben Sie eine gültige E-Mail ein.';
    if (!form.datenschutz) e.datenschutz = 'Bitte stimmen Sie der Datenschutzerklärung zu.';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSubmitted(true);
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-28 bg-[#2A2A2A]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <span className="text-[11px] tracking-[0.22em] uppercase text-[#C4944A] font-outfit mb-3 block">
            Kontakt
          </span>
          <h2 className="font-cormorant text-4xl md:text-5xl text-white font-medium mb-5">
            Wir sind für Sie da.
          </h2>
          <p className="font-outfit text-base text-white/55 max-w-lg mx-auto">
            Rufen Sie uns an oder schreiben Sie uns — wir melden uns schnellstmöglich bei Ihnen.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Contact info + map */}
          <motion.div
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            {/* Phone CTA */}
            <a
              href="tel:+493043115080"
              data-testid="contact-phone-big"
              className="block bg-[#8B7355] p-7 group hover:bg-[#6B5440] transition-colors duration-200"
            >
              <div className="flex items-center gap-4">
                <Phone size={20} className="text-white/70" />
                <div>
                  <span className="block font-outfit text-[10px] tracking-[0.2em] uppercase text-white/55 mb-1">
                    Telefon
                  </span>
                  <span className="font-cormorant text-3xl text-white font-semibold">
                    030 / 4 31 15 80
                  </span>
                </div>
              </div>
            </a>

            {/* Info cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-[#3A3A3A] border border-white/8 p-5" data-testid="contact-address">
                <MapPin size={15} className="text-[#C4944A] mb-3" />
                <span className="font-outfit text-[10px] tracking-[0.15em] uppercase text-white/40 mb-1 block">Adresse</span>
                <p className="font-outfit text-sm text-white/80 leading-relaxed">
                  An der Schneise 57<br />
                  13503 Berlin-Heiligensee
                </p>
              </div>
              <div className="bg-[#3A3A3A] border border-white/8 p-5" data-testid="contact-hours">
                <Clock size={15} className="text-[#C4944A] mb-3" />
                <span className="font-outfit text-[10px] tracking-[0.15em] uppercase text-white/40 mb-1 block">Öffnungszeiten</span>
                <p className="font-outfit text-sm text-white/80 leading-relaxed">
                  Mo – Fr<br />
                  08:30 – 13:00 Uhr<br />
                  14:00 – 17:00 Uhr
                </p>
              </div>
              <div className="bg-[#3A3A3A] border border-white/8 p-5 sm:col-span-2" data-testid="contact-email">
                <Mail size={15} className="text-[#C4944A] mb-3" />
                <span className="font-outfit text-[10px] tracking-[0.15em] uppercase text-white/40 mb-1 block">E-Mail</span>
                <a href="mailto:gardinenatelier@ausheiligensee.de" className="font-outfit text-sm text-white/80 hover:text-[#C4944A] transition-colors">
                  gardinenatelier@ausheiligensee.de
                </a>
                <p className="font-outfit text-xs text-white/40 mt-2">
                  Beratungstermine bitte telefonisch vereinbaren
                </p>
              </div>
            </div>

            {/* OpenStreetMap */}
            <div className="border border-white/10 overflow-hidden" data-testid="contact-map">
              <iframe
                src={MAP_URL}
                width="100%"
                height="220"
                style={{ border: 0, display: 'block' }}
                title="Schulz & Gojowy Standort"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="bg-[#F8F5F0] p-8 sm:p-10"
            data-testid="contact-form-wrapper"
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center" data-testid="contact-success">
                <CheckCircle size={40} className="text-[#8B7355] mb-5" />
                <h3 className="font-cormorant text-2xl text-[#2A2A2A] mb-3">Vielen Dank!</h3>
                <p className="font-outfit text-sm text-[#2A2A2A]/65">
                  Ihre Nachricht wurde übermittelt. Wir melden uns so schnell wie möglich bei Ihnen.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate data-testid="contact-form">
                <h3 className="font-cormorant text-2xl text-[#2A2A2A] mb-7 font-semibold">
                  Nachricht senden
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="font-outfit text-xs tracking-wide text-[#2A2A2A]/65 mb-1.5 block" htmlFor="name">
                      Ihr Name *
                    </label>
                    <input
                      id="name"
                      type="text"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={`w-full border bg-white px-4 py-3 font-outfit text-sm text-[#2A2A2A] focus:outline-none focus:border-[#8B7355] transition-colors ${errors.name ? 'border-red-400' : 'border-[#EDE8E0]'}`}
                      placeholder="Max Mustermann"
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="font-outfit text-xs tracking-wide text-[#2A2A2A]/65 mb-1.5 block" htmlFor="email">
                      E-Mail-Adresse *
                    </label>
                    <input
                      id="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full border bg-white px-4 py-3 font-outfit text-sm text-[#2A2A2A] focus:outline-none focus:border-[#8B7355] transition-colors ${errors.email ? 'border-red-400' : 'border-[#EDE8E0]'}`}
                      placeholder="mail@beispiel.de"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="font-outfit text-xs tracking-wide text-[#2A2A2A]/65 mb-1.5 block" htmlFor="telefon">
                      Telefon
                    </label>
                    <input
                      id="telefon"
                      type="tel"
                      data-testid="contact-input-telefon"
                      value={form.telefon}
                      onChange={(e) => handleChange('telefon', e.target.value)}
                      className="w-full border border-[#EDE8E0] bg-white px-4 py-3 font-outfit text-sm text-[#2A2A2A] focus:outline-none focus:border-[#8B7355] transition-colors"
                      placeholder="030 / ..."
                    />
                  </div>
                  <div>
                    <label className="font-outfit text-xs tracking-wide text-[#2A2A2A]/65 mb-1.5 block" htmlFor="anliegen">
                      Anliegen
                    </label>
                    <select
                      id="anliegen"
                      data-testid="contact-select-anliegen"
                      value={form.anliegen}
                      onChange={(e) => handleChange('anliegen', e.target.value)}
                      className="w-full border border-[#EDE8E0] bg-white px-4 py-3 font-outfit text-sm text-[#2A2A2A] focus:outline-none focus:border-[#8B7355] transition-colors"
                    >
                      <option value="">Bitte wählen ...</option>
                      {anliegen.map((a) => (
                        <option key={a} value={a.toLowerCase()}>{a}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="font-outfit text-xs tracking-wide text-[#2A2A2A]/65 mb-1.5 block" htmlFor="nachricht">
                    Ihre Nachricht
                  </label>
                  <textarea
                    id="nachricht"
                    data-testid="contact-textarea-nachricht"
                    value={form.nachricht}
                    onChange={(e) => handleChange('nachricht', e.target.value)}
                    rows={4}
                    className="w-full border border-[#EDE8E0] bg-white px-4 py-3 font-outfit text-sm text-[#2A2A2A] focus:outline-none focus:border-[#8B7355] transition-colors resize-none"
                    placeholder="Wie können wir Ihnen helfen?"
                  />
                </div>

                <div className="mb-7">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-datenschutz-label">
                    <input
                      type="checkbox"
                      data-testid="contact-checkbox-datenschutz"
                      checked={form.datenschutz}
                      onChange={(e) => handleChange('datenschutz', e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#8B7355] flex-shrink-0"
                    />
                    <span className="font-outfit text-xs text-[#2A2A2A]/60 leading-relaxed">
                      Ich habe die{' '}
                      <a href="/datenschutz" className="text-[#8B7355] hover:underline">
                        Datenschutzerklärung
                      </a>{' '}
                      gelesen und stimme der Verarbeitung meiner Daten zu. *
                    </span>
                  </label>
                  {errors.datenschutz && (
                    <p className="text-red-500 text-xs mt-1">{errors.datenschutz}</p>
                  )}
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#8B7355] text-white py-4 font-outfit text-xs tracking-[0.18em] uppercase hover:bg-[#6B5440] transition-colors duration-200"
                >
                  Nachricht senden
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
